// The WebGPU instance buffer: one `Float32Array` and one `GPUBuffer` of
// divisor-1 attributes (the runtime design §1).
//
// It is the WebGPU twin of `gl/instances.ts`. The CPU half is the shared
// `gfx/instance-array.ts` — the batch above it writes floats and calls
// `reserve` — and the GPU half is
// not: there is no vertex array object and no divisor, because the STEP MODE
// rides the pipeline's vertex layout (`pipeline.ts` `vertexShape`).
//
// **Location 0 belongs to the corner** (R553), so the buffer is bound at
// vertex slot 1 and the corner at slot 0. Both slots are set on every draw,
// because a WebGPU render pass keeps no such state across pipelines.
//
// It survives a device loss: the CPU array is kept, and `create` builds the
// buffer again from it.

import { BUFFER_COPY_DST, BUFFER_VERTEX } from './flags';
import { corners, type GpuCornerBuffer } from './corner';
import { InstanceArray, STRIP_VERTICES } from '../gfx/instance-array';
import type { AttribSpec, InstanceBuffer as InstanceBufferApi } from '../gfx/types';
import type { Gpu, GpuResource } from './context';

export class GpuInstanceBuffer extends InstanceArray implements GpuResource, InstanceBufferApi {
  private vbo: GPUBuffer | null = null;
  private readonly corner: GpuCornerBuffer;

  // The attribute list rides the pipeline's vertex layout, so the buffer
  // takes it for the one factory signature and reads nothing from it.
  constructor(
    private readonly gpu: Gpu,
    floatsPerInstance: number,
    _attribs: readonly AttribSpec[],
    capacity: number,
  ) {
    super(floatsPerInstance, capacity);
    // The corner buffer registers first, so a restore builds it before this
    // buffer's first draw binds it (R553).
    this.corner = corners(gpu);
    gpu.register(this);
  }

  create(device: GPUDevice): void {
    // A growth runs inside an open frame, so the old buffer is retired (R1340).
    if (this.vbo) this.gpu.retire(this.vbo);
    this.vbo = device.createBuffer({
      label: 'fast: instances',
      size: Math.max(4, this.data.byteLength),
      usage: BUFFER_VERTEX | BUFFER_COPY_DST,
    });
  }

  destroy(): void {
    if (this.vbo) this.vbo.destroy();
    this.vbo = null;
  }

  // A WebGPU buffer has no resize: the growth builds a new one, which is
  // what `create` does anyway after a device restore.
  protected rebuild(): void {
    if (!this.gpu.lost) this.create(this.gpu.device);
  }

  /**
   * Uploads the written instances and draws them as one instanced strip.
   *
   * The upload is ONE `queue.writeBuffer`. A queue write made before the
   * frame is submitted lands in front of every command of that frame, so a
   * batch writes and draws once a frame, which is what every batch does.
   */
  draw(vertexCount: number = STRIP_VERTICES): number {
    const gpu = this.gpu;
    if (this.count === 0 || !this.vbo || gpu.lost) return 0;
    const pass = gpu.current?.bindDraw();
    if (!pass) return 0;
    const corner = this.corner.buffer;
    if (!corner) return 0;
    const floats = this.count * this.floatsPerInstance;
    gpu.queue.writeBuffer(this.vbo, 0, this.data.buffer, this.data.byteOffset, floats * 4);
    pass.setVertexBuffer(0, corner);
    pass.setVertexBuffer(1, this.vbo);
    pass.draw(vertexCount, this.count);
    gpu.drawCalls++;
    gpu.instances += this.count;
    return this.count;
  }

  dispose(): void { this.gpu.unregister(this); }
}
